import { FileLayout, FileTypes, SampleFrequency } from './SampleData';

export type CombinedSample = {
  download: { tag: string; type: FileTypes }[];
  layout: FileLayout;
  title: string;
  description: string;
  frequency?: SampleFrequency[];
  info?: string;
};

export const SampleReturnsData: CombinedSample[] = [
  {
    download: [
      { tag: 'vertical_layout_sample_xls', type: FileTypes.EXCEL },
      { tag: 'vertical_layout_sample_csv', type: FileTypes.CSV },
      { tag: 'vertical_layout_sample_pdf', type: FileTypes.PDF },
    ],
    layout: FileLayout.VERTICAL,
    title: 'Vertical Template',
    description: 'File with a single date column and multiple investment columns.',
    frequency: [SampleFrequency.DAILY, SampleFrequency.MONTHLY, SampleFrequency.QUARTERLY],
  },
  {
    download: [
      { tag: 'horizontal_layout_sample_xls', type: FileTypes.EXCEL },
      { tag: 'horizontal_layout_sample_csv', type: FileTypes.CSV },
      { tag: 'horizontal_layout_sample_pdf', type: FileTypes.PDF },
    ],
    layout: FileLayout.HORIZONTAL,
    title: 'Horizontal Template',
    description: 'File with a row of dates and a column for investment names.',
    frequency: [SampleFrequency.DAILY, SampleFrequency.MONTHLY, SampleFrequency.QUARTERLY],
  },
  {
    download: [
      { tag: 'database_layout_sample_xls', type: FileTypes.EXCEL },
      { tag: 'database_layout_sample_csv', type: FileTypes.CSV },
      { tag: 'database_layout_sample_pdf', type: FileTypes.PDF },
    ],
    layout: FileLayout.DATABASE,
    title: 'Database Template',
    description: 'File with a date column, investment name column and return column.',
    frequency: [SampleFrequency.DAILY, SampleFrequency.MONTHLY, SampleFrequency.QUARTERLY],
  },
  {
    download: [
      { tag: 'multi_table_sample_xls', type: FileTypes.EXCEL },
      { tag: 'multi_table_sample_csv', type: FileTypes.CSV },
    ],
    layout: FileLayout.MULTIDATE,
    title: 'Multi-Table Template',
    description: 'File with multiple tables containing a date and investment column each.',
    frequency: [SampleFrequency.DAILY, SampleFrequency.MONTHLY, SampleFrequency.QUARTERLY],
  },
  {
    download: [
      { tag: 'returns_grid_sample_xls', type: FileTypes.EXCEL },
      { tag: 'returns_grid_sample_csv', type: FileTypes.CSV },
      { tag: 'returns_grid_sample_pdf', type: FileTypes.PDF },
    ],
    layout: FileLayout.GRID,
    title: 'Returns Grid Template',
    description:
      'File with monthly returns in a calendar grid format, with years in one column, and months in a row across.',
    frequency: [SampleFrequency.MONTHLY],
  },
];

export const SamplePrivatesData: CombinedSample[] = [
  {
    download: [{ tag: 'private_sample_xls', type: FileTypes.EXCEL }],
    layout: FileLayout.HORIZONTAL,
    title: 'Full Cash Flow History Template',
    description: 'Template for private capital investments with full historical cashflow transactions.',
    info: 'Note: Instructions provided on first tab of the Excel',
  },
  {
    download: [{ tag: 'cumulative_private_sample_xls', type: FileTypes.EXCEL }],
    layout: FileLayout.HORIZONTAL,
    title: 'Cumulative Cash Flow History Template',
    description:
      'Template for private capital investments with the most recent Value and Cumulative Contributions - useful for investments whose historical cashflow transactions are not available.',
    info: 'Note: Instructions provided on first tab of the Excel',
  },
];

export const SampleNavData: CombinedSample[] = [
  {
    download: [{ tag: 'sample_navs', type: FileTypes.EXCEL }],
    layout: FileLayout.VERTICAL,
    title: 'Nav Template',
    description: 'File with portfolio allocations as rows.',
  },
];

export const SamplePortfolioData: CombinedSample[] = [
  {
    download: [{ tag: 'sample_multi_portfolio', type: FileTypes.EXCEL }],
    layout: FileLayout.MULTI_PORTFOLIO_PROFORMA,
    title: 'Pro Forma Portfolio Template',
    description: 'File with multiple pro forma portfolios that supports nested strategies.',
  },
];

export const SampleHistoricalPortfoliosData: CombinedSample[] = [
  {
    download: [
      { tag: 'sample_historical_portfolio_xlsx', type: FileTypes.EXCEL },
      { tag: 'sample_historical_portfolio_csv', type: FileTypes.CSV },
    ],
    layout: FileLayout.MULTI_PORTFOLIO_HISTORICAL,
    title: 'Historical Allocation Vertical Template',
    description: 'File with multiple portfolios with historical allocations that supports nested strategies.',
  },
  {
    download: [
      { tag: 'sample_historical_portfolio_horizontal_xlsx', type: FileTypes.EXCEL },
      { tag: 'sample_historical_portfolio_horizontal_csv', type: FileTypes.CSV },
    ],
    layout: FileLayout.MULTI_PORTFOLIO_HISTORICAL_HORIZONTAL,
    title: 'Historical Allocation Horizontal Template',
    description: 'File with multiple portfolios with historical allocations that supports nested strategies.',
  },
];

export const StrategyEnforcementSamplePortfolioData: CombinedSample[] = [
  {
    download: [{ tag: 'rcm_sample_multi_portfolio', type: FileTypes.EXCEL }],
    layout: FileLayout.MULTI_PORTFOLIO_PROFORMA,
    title: 'Pro Forma Portfolio Template',
    description: 'File with multiple pro forma portfolios.',
  },
];

export const StrategyEnforcementSampleHistoricalPortfoliosData: CombinedSample[] = [
  {
    download: [
      { tag: 'rcm_sample_historical_portfolio_xlsx', type: FileTypes.EXCEL },
      { tag: 'rcm_sample_historical_portfolio_csv', type: FileTypes.CSV },
    ],
    layout: FileLayout.MULTI_PORTFOLIO_HISTORICAL,
    title: 'Historical Allocation Vertical Template',
    description: 'File with multiple portfolios with historical allocations.',
  },
  {
    download: [
      { tag: 'rcm_sample_historical_portfolio_horizontal_xlsx', type: FileTypes.EXCEL },
      { tag: 'rcm_sample_historical_portfolio_horizontal_csv', type: FileTypes.CSV },
    ],
    layout: FileLayout.MULTI_PORTFOLIO_HISTORICAL_HORIZONTAL,
    title: 'Historical Allocation Horizontal Template',
    description: 'File with multiple portfolios with historical allocations.',
  },
];

export const TaxAnalysisSampleData: CombinedSample[] = [
  {
    download: [
      { tag: 'sample_tax_analysis_xlsx', type: FileTypes.EXCEL },
      { tag: 'sample_tax_analysis_csv', type: FileTypes.CSV },
    ],
    layout: FileLayout.MULTI_PORTFOLIO_PROFORMA,
    title: 'Tax Analysis Template',
    description: 'File with multiple pro forma portfolios with tax information that supports nested strategies.',
  },
];

export const StrategyEnforcementTaxAnalysisSampleData: CombinedSample[] = [
  {
    download: [
      { tag: 'rcm_sample_tax_analysis_xlsx', type: FileTypes.EXCEL },
      { tag: 'rcm_sample_tax_analysis_csv', type: FileTypes.CSV },
    ],
    layout: FileLayout.MULTI_PORTFOLIO_PROFORMA,
    title: 'Tax Analysis Template',
    description: 'File with multiple pro forma portfolios with tax information that supports nested strategies.',
  },
];
